/**
 * Pure functions for leveraged-loop math. No side effects, no hooks.
 *
 * Loop economics
 * --------------
 * principal P, target leverage L (e.g. 3.0×), LTV cap on the lending venue.
 * Effective collateral C  = P · L                       (collateral units)
 * Total borrow      B     = P · (L − 1) · price          (loan/USDC units)
 * Net principal at risk   = P
 *
 * The geometry (collateral, leverage, iterations) is price-agnostic, but the
 * borrow is drawn in the loan asset (USDC), so `collateralPriceUsdc` converts
 * the collateral-denominated borrow into USDC for `totalBorrow` and slippage.
 *
 * After N geometric iterations of (deposit → borrow → swap), the realised
 * leverage approaches  1 / (1 − ltv)  asymptotically. We compute the number
 * of iterations needed to reach `targetLeverage` within a small epsilon, and
 * report the resulting effective leverage.
 */

export type LoopParams = {
  readonly principal: number;
  readonly targetLeverage: number;
  readonly ltv: number;
  readonly slippageBps: number;
  /** USDC per 1 collateral token (CADC). Converts borrow into USDC units. */
  readonly collateralPriceUsdc: number;
};

export type LoopProjection = {
  readonly totalCollateral: number;
  readonly totalBorrow: number;
  readonly iterations: number;
  readonly effectiveLeverage: number;
  readonly maxLeverage: number;
  readonly liquidationBuffer: number;
  readonly estSlippageCost: number;
};

const EPSILON = 0.001;
const MAX_ITERATIONS = 12;

const clamp = (n: number, lo: number, hi: number): number =>
  Math.min(Math.max(n, lo), hi);

export const maxLeverageForLtv = (ltv: number): number =>
  ltv >= 1 ? Infinity : 1 / (1 - ltv);

const leverageAfterIterations = (n: number, ltv: number): number => {
  if (ltv >= 1) return Infinity;
  return (1 - ltv ** (n + 1)) / (1 - ltv);
};

export const iterationsToReach = (
  targetLeverage: number,
  ltv: number,
): number => {
  const cap = maxLeverageForLtv(ltv);
  const target = clamp(targetLeverage, 1, cap - EPSILON);
  for (let n = 0; n <= MAX_ITERATIONS; n += 1) {
    if (leverageAfterIterations(n, ltv) >= target) return n;
  }
  return MAX_ITERATIONS;
};

export const projectLoop = (params: LoopParams): LoopProjection => {
  const { principal, targetLeverage, ltv, slippageBps, collateralPriceUsdc } =
    params;
  const cap = maxLeverageForLtv(ltv);
  const realised = clamp(targetLeverage, 1, cap - EPSILON);

  const totalCollateral = principal * realised;
  // Borrow is drawn in USDC: collateral-denominated amount × CADC price.
  const totalBorrow =
    principal * Math.max(0, realised - 1) * collateralPriceUsdc;
  const iterations = iterationsToReach(realised, ltv);
  const liquidationBuffer = ltv === 0 ? 1 : 1 - realised / cap;
  const estSlippageCost = (totalBorrow * slippageBps) / 10_000;

  return {
    totalCollateral,
    totalBorrow,
    iterations,
    effectiveLeverage: realised,
    maxLeverage: cap,
    liquidationBuffer,
    estSlippageCost,
  };
};
